import { getToken } from '../../Utils/authUtils';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { REPORTS } from '../../constant';
import { getHospitalId } from '../../Utils/authUtils';

/**
 * Report labels — the clinic's own workflow markers on the reports list.
 *
 * Receptionists download finalized reports and file them into Best Practice by hand, and
 * nothing recorded which ones were already done. These endpoints back that column.
 *
 * All five thunks live in one file rather than the usual one-per-file split: they are a
 * single small CRUD surface over one resource, and splitting them would spread ~20 lines
 * across five files with no separate consumer.
 */

export interface ReportLabel {
  _id: string;
  name: string;
  color?: string;
  hospitalId?: string;
}

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`,
});

/** Every request carries hospitalId; the server still prefers the token's own tenant. */
const withTenant = (body: Record<string, unknown> = {}) => ({
  ...body,
  hospitalId: getHospitalId(),
});

export const getReportLabels = createAsyncThunk(
  'reports/getReportLabels',
  async (_: void, { rejectWithValue }) => {
    try {
      const url = `${REPORTS.REPORT_LABELS}?hospitalId=${encodeURIComponent(getHospitalId() || '')}`;
      const response = await fetch(url, { method: 'GET', headers: authHeaders() });
      if (!response.ok) {
        const err = await response.json();
        return rejectWithValue(err.msg || 'Failed to load labels');
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createReportLabel = createAsyncThunk(
  'reports/createReportLabel',
  async (payload: { name: string; color?: string }, { rejectWithValue }) => {
    try {
      const response = await fetch(REPORTS.REPORT_LABELS, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(withTenant({ name: payload.name, color: payload.color })),
      });
      if (!response.ok) {
        const err = await response.json();
        return rejectWithValue(err.msg || 'Failed to create label');
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateReportLabel = createAsyncThunk(
  'reports/updateReportLabel',
  async (payload: { labelId: string; name?: string; color?: string }, { rejectWithValue }) => {
    try {
      const response = await fetch(REPORTS.REPORT_LABEL_BY_ID(payload.labelId), {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(withTenant({ name: payload.name, color: payload.color })),
      });
      if (!response.ok) {
        const err = await response.json();
        return rejectWithValue(err.msg || 'Failed to rename label');
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteReportLabel = createAsyncThunk(
  'reports/deleteReportLabel',
  async (payload: { labelId: string }, { rejectWithValue }) => {
    try {
      const url = `${REPORTS.REPORT_LABEL_BY_ID(payload.labelId)}?hospitalId=${encodeURIComponent(
        getHospitalId() || ''
      )}`;
      const response = await fetch(url, { method: 'DELETE', headers: authHeaders() });
      if (!response.ok) {
        const err = await response.json();
        return rejectWithValue(err.msg || 'Failed to remove label');
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/** Replace the label set on one report. */
export const setReportLabels = createAsyncThunk(
  'reports/setReportLabels',
  async (payload: { reportId: string; labels: string[] }, { rejectWithValue }) => {
    try {
      const response = await fetch(REPORTS.SET_REPORT_LABELS(payload.reportId), {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(withTenant({ labels: payload.labels })),
      });
      if (!response.ok) {
        const err = await response.json();
        return rejectWithValue(err.msg || 'Failed to update labels');
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
